import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import {
  CreditCard,
  CheckCircle,
  AlertCircle,
  XCircle,
  Database,
  Globe,
  HardDrive,
  Zap,
  Users,
  Activity,
  Server,
  Cpu
} from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function formatCurrency(amount, currency = 'USD') {
  if (amount == null) return '—';
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency }).format(amount / 100);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-CL', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function InvoiceStatusBadge({ status }) {
  const config = {
    paid: { label: 'Pagada', variant: 'default', icon: CheckCircle },
    open: { label: 'Pendiente', variant: 'secondary', icon: AlertCircle },
    overdue: { label: 'Vencida', variant: 'destructive', icon: XCircle },
    draft: { label: 'Borrador', variant: 'outline', icon: Activity },
    void: { label: 'Anulada', variant: 'outline', icon: XCircle }
  };
  const c = config[status?.toLowerCase()] || { label: status || '—', variant: 'outline', icon: Activity };
  const Icon = c.icon;
  return (
    <Badge variant={c.variant} className="flex items-center gap-1 text-xs">
      <Icon className="h-3 w-3" />
      {c.label}
    </Badge>
  );
}

function StatusIcon({ status }) {
  if (status === 'ok' || status === 'ACTIVE_HEALTHY') return <CheckCircle className="h-4 w-4 text-green-500" />;
  if (status === 'error' || status === 'INACTIVE') return <XCircle className="h-4 w-4 text-red-500" />;
  return <AlertCircle className="h-4 w-4 text-yellow-500" />;
}

// ─── Supabase Health Section ─────────────────────────────────────────────────

function SupabaseHealthSection({ data }) {
  if (!data || !data.projects) return null;

  const serviceLabel = {
    db: 'Database',
    storage: 'Storage',
    auth: 'Auth',
    realtime: 'Realtime',
    rest: 'REST API',
    edge_runtime: 'Edge Functions',
    pooler: 'Pooler',
    pgbouncer: 'PgBouncer'
  };

  const statusColor = (s) => {
    if (!s) return 'text-muted-foreground';
    if (s === 'ACTIVE_HEALTHY') return 'text-[var(--yool-success)]';
    if (s === 'ACTIVE_UNHEALTHY' || s === 'COMING_UP') return 'text-yellow-600';
    return 'text-[var(--yool-error)]';
  };

  return (
    <div className="space-y-4">
      {data.projects.map((proj, idx) => (
        <Card key={idx}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Server className="h-4 w-4" />
              Proyecto: <span className="capitalize font-semibold">{proj.name}</span>
              {proj.info?.status && (
                <Badge
                  variant={proj.info.status === 'ACTIVE_HEALTHY' ? 'default' : 'destructive'}
                  className="text-xs capitalize"
                >
                  {proj.info.status.replace(/_/g, ' ')}
                </Badge>
              )}
            </CardTitle>
            {proj.info && (
              <CardDescription>
                Región: {proj.info.region || '—'} · Host: {proj.info.database_host || '—'}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {proj.healthError ? (
              <p className="text-xs text-destructive">{proj.healthError}</p>
            ) : proj.health && Array.isArray(proj.health) && proj.health.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {proj.health.map((svc, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 border rounded-md">
                    <StatusIcon status={svc.status} />
                    <div>
                      <p className="text-xs font-medium">{serviceLabel[svc.name] || svc.name}</p>
                      <p className={`text-xs ${statusColor(svc.status)}`}>
                        {svc.status?.replace(/_/g, ' ') || '—'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Sin datos de health disponibles</p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Supabase Billing Section ────────────────────────────────────────────────

function SupabaseBillingSection({ data }) {
  if (!data) return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>No se pudo cargar datos de Supabase</AlertDescription>
    </Alert>
  );

  if (data.status === 'error') return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>Error Supabase: {data.error}</AlertDescription>
    </Alert>
  );

  const { subscription, invoices = [], subscriptionError, invoicesError, projectUsages = [] } = data;

  const overdueInvoices = invoices.filter(inv =>
    inv.status === 'overdue' || (inv.status === 'open' && inv.due_date && new Date(inv.due_date) < new Date())
  );
  const pendingInvoices = invoices.filter(inv => inv.status === 'open');
  const hasIssues = overdueInvoices.length > 0;

  return (
    <div className="space-y-4">
      {hasIssues && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Hay {overdueInvoices.length} factura(s) vencida(s) en Supabase. Revisar pago inmediatamente.
          </AlertDescription>
        </Alert>
      )}
      {pendingInvoices.length > 0 && !hasIssues && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {pendingInvoices.length} factura(s) pendiente(s) de pago en Supabase.
          </AlertDescription>
        </Alert>
      )}

      {/* Suscripción */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Suscripción Supabase
          </CardTitle>
        </CardHeader>
        <CardContent>
          {subscriptionError ? (
            <p className="text-sm text-destructive">{subscriptionError}</p>
          ) : subscription ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Plan</p>
                <p className="font-semibold capitalize">{subscription.plan_name || subscription.tier || 'Pro'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Estado</p>
                <Badge variant={subscription.status === 'active' ? 'default' : 'destructive'} className="capitalize">
                  {subscription.status || 'active'}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Período actual</p>
                <p className="text-sm">
                  {formatDate(subscription.current_period_start)} — {formatDate(subscription.current_period_end)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Monto</p>
                <p className="font-semibold">
                  {subscription.amount != null ? formatCurrency(subscription.amount, subscription.currency) : '—'}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Organización: yooldigital · Plan Pro</p>
          )}
        </CardContent>
      </Card>

      {/* Facturas */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Facturas Supabase</CardTitle>
          <CardDescription>
            {invoicesError ? invoicesError : `${invoices.length} facturas encontradas`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Sin facturas disponibles</p>
          ) : (
            <div className="space-y-2">
              {invoices.slice(0, 8).map((inv, idx) => (
                <div key={inv.id || idx} className="flex items-center justify-between p-2 border rounded-md">
                  <div className="flex items-center gap-3">
                    <InvoiceStatusBadge status={inv.status} />
                    <div>
                      <p className="text-sm font-medium">{inv.number || inv.id || `#${idx + 1}`}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(inv.period_start || inv.created_at)} · Vence: {formatDate(inv.due_date)}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm font-semibold">
                    {formatCurrency(inv.amount_due ?? inv.total, inv.currency)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage por proyecto */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Database className="h-4 w-4" />
            Uso por Proyecto
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projectUsages.map((proj, idx) => (
              <div key={idx} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StatusIcon status={proj.status} />
                    <p className="font-medium capitalize">{proj.data?.name || `Proyecto ${idx + 1}`}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {proj.data?.id?.substring(0, 8) || '—'}
                  </Badge>
                </div>
                {proj.status === 'error' ? (
                  <p className="text-xs text-destructive">{proj.error}</p>
                ) : proj.data?.usage ? (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {proj.data.usage.db_size != null && (
                      <UsageStat icon={HardDrive} label="DB Size" value={formatBytes(proj.data.usage.db_size)} />
                    )}
                    {proj.data.usage.monthly_active_users != null && (
                      <UsageStat icon={Users} label="MAU" value={proj.data.usage.monthly_active_users} />
                    )}
                    {proj.data.usage.bandwidth != null && (
                      <UsageStat icon={Activity} label="Bandwidth" value={formatBytes(proj.data.usage.bandwidth)} />
                    )}
                    {proj.data.usage.function_invocations != null && (
                      <UsageStat icon={Zap} label="Edge Func." value={proj.data.usage.function_invocations?.toLocaleString()} />
                    )}
                    {proj.data.usage.storage_size != null && (
                      <UsageStat icon={HardDrive} label="Storage" value={formatBytes(proj.data.usage.storage_size)} />
                    )}
                    {proj.data.usage.realtime_message_count != null && (
                      <UsageStat icon={Activity} label="Realtime msgs" value={proj.data.usage.realtime_message_count?.toLocaleString()} />
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Sin datos de uso disponibles</p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function UsageStat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="h-3 w-3 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground">{label}:</span>
      <span className="text-xs font-medium">{value}</span>
    </div>
  );
}

// ─── Vercel Billing Section ──────────────────────────────────────────────────

function VercelBillingSection({ data }) {
  if (!data) return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>No se pudo cargar datos de Vercel</AlertDescription>
    </Alert>
  );

  if (data.status === 'error') return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>Error Vercel: {data.error}</AlertDescription>
    </Alert>
  );

  const { subscription, subscriptionError, invoices = [], invoicesError } = data;

  return (
    <div className="space-y-4">
      {/* Suscripción */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Suscripción Vercel
          </CardTitle>
        </CardHeader>
        <CardContent>
          {subscriptionError ? (
            <p className="text-sm text-muted-foreground">
              Datos de suscripción no disponibles vía API. {subscriptionError}
            </p>
          ) : subscription ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {(subscription.plan || subscription.name) && (
                <div>
                  <p className="text-xs text-muted-foreground">Plan</p>
                  <p className="font-semibold capitalize">{subscription.plan || 'Pro'}</p>
                </div>
              )}
              {subscription.slug && (
                <div>
                  <p className="text-xs text-muted-foreground">Team</p>
                  <p className="text-sm font-medium">{subscription.name || subscription.slug}</p>
                </div>
              )}
              {subscription.status && (
                <div>
                  <p className="text-xs text-muted-foreground">Estado</p>
                  <Badge variant={subscription.status === 'active' ? 'default' : 'secondary'} className="capitalize">
                    {subscription.status}
                  </Badge>
                </div>
              )}
              {subscription.period?.start && (
                <div>
                  <p className="text-xs text-muted-foreground">Período</p>
                  <p className="text-sm">
                    {formatDate(subscription.period.start)} — {formatDate(subscription.period.end)}
                  </p>
                </div>
              )}
              {subscription.amount != null && (
                <div>
                  <p className="text-xs text-muted-foreground">Monto</p>
                  <p className="font-semibold">{formatCurrency(subscription.amount, subscription.currency)}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Información de suscripción no disponible vía API pública</p>
          )}
        </CardContent>
      </Card>

      {/* Facturas Vercel */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Facturas Vercel</CardTitle>
          <CardDescription>
            {invoicesError
              ? `No disponible: ${invoicesError}`
              : `${invoices.length} factura(s) encontrada(s)`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {invoicesError ? 'API de facturas no disponible en plan actual' : 'Sin facturas disponibles'}
            </p>
          ) : (
            <div className="space-y-2">
              {invoices.slice(0, 8).map((inv, idx) => (
                <div key={inv.id || idx} className="flex items-center justify-between p-2 border rounded-md">
                  <div className="flex items-center gap-3">
                    <InvoiceStatusBadge status={inv.status} />
                    <div>
                      <p className="text-sm font-medium">{inv.number || inv.id || `#${idx + 1}`}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(inv.period_start || inv.created)} · Vence: {formatDate(inv.due_date)}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm font-semibold">
                    {formatCurrency(inv.total ?? inv.amount_due, inv.currency)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

const BillingOverview = ({ billingData, supabaseHealth, isLoading }) => {
  if (isLoading) {
    return (
      <div className="space-y-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-3/4" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!billingData) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>No hay datos de billing disponibles. Intenta actualizar.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-8">
      {/* Supabase Health */}
      {supabaseHealth && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Cpu className="h-5 w-5 text-[var(--yool-success)]" />
            <h2 className="text-lg font-semibold">Supabase · Health Status</h2>
            <Badge variant="outline" className="text-xs">yooldigital</Badge>
          </div>
          <SupabaseHealthSection data={supabaseHealth} />
        </div>
      )}

      {/* Supabase Billing */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-[var(--yool-success)]" />
          <h2 className="text-lg font-semibold">Supabase · Billing & Usage</h2>
          <Badge variant="outline" className="text-xs">yooldigital · Pro</Badge>
        </div>
        <SupabaseBillingSection data={billingData.supabase} />
      </div>

      {/* Vercel Billing */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-black dark:text-white" />
          <h2 className="text-lg font-semibold">Vercel · Billing</h2>
        </div>
        <VercelBillingSection data={billingData.vercel} />
      </div>

      {billingData.lastUpdated && (
        <p className="text-xs text-muted-foreground">
          Datos obtenidos: {new Date(billingData.lastUpdated).toLocaleString('es-CL')}
        </p>
      )}
    </div>
  );
};

export default BillingOverview;
